import { useAnalytics } from "@codat/analytics";
import { Button, Checkbox, useToast } from "@codat/orchard-ui";
import { loginByPasswordMutation } from "api/login";
import { AxiosError } from "axios";
import React, { FormEvent, useRef, useState } from "react";
import { ApiError } from "shared";
import formatUrl from "utils/formatUrl";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import EmailInput from "components/EmailInput";
import PasswordInput from "components/PasswordInput";
import { LoginProps } from "../Login";
import styles from "./LoginByPassword.module.css";
import config from "config";

export const LoginByPassword: React.FC<LoginProps> = ({
  redirectUrl,
  inviteId,
  turnstileSiteKey,
}) => {
  const [, loginByPasswordApi] = loginByPasswordMutation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const [emailIsValid, setEmailIsValid] = useState(false);
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showTurnstile, setShowTurnstile] = useState<boolean>(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const handleRememberMeClick = (e: React.ChangeEvent<HTMLInputElement>) =>
    setRememberMe(e.target.checked);

  const login = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!emailIsValid || !passwordIsValid) {
      setDisplayErrors(true);
      return;
    }
    setIsSigningIn(true);

    loginByPasswordApi({
      data: {
        email,
        password,
        rememberMe,
        redirectUrl,
        inviteId,
        turnstileToken,
      },
    })
      .then((response) => {
        window.location.href = response.data.loginRedirect;
      })
      .catch((error: AxiosError<ApiError>) => {
        const errorMessage = error.response?.data?.error ?? "Unknown error";
        const match = errorMessage.match(/(\d+)/);
        if (match) {
          const attemptsRemaining = parseInt(match[1], 10);
          attemptsRemaining <= 5 && setShowTurnstile(true);
        } else {
          setShowTurnstile(true);
        }
        addToast({
          message: `Unable to sign in: ${errorMessage}`,
          variant: "error",
        });
        setIsSigningIn(false);
        setTurnstileToken(null);
        turnstileRef.current?.reset();
      });
  };

  return (
    <>
      <form onSubmit={login}>
        <EmailInput
          data-testid="email-input"
          setEmail={setEmail}
          value={email}
          isEmailValid={setEmailIsValid}
          displayErrors={displayErrors}
        />
        <PasswordInput
          label="Password"
          inputName="Password Input"
          id="password-input"
          data-testid="password-input"
          setPassword={setPassword}
          isPasswordValid={setPasswordIsValid}
          displayErrors={displayErrors}
          placeholder=""
          className={styles.passwordInputGroup}
        />
        {showTurnstile && (
          <div data-testid="turnstile">
            <Turnstile
              siteKey={turnstileSiteKey ?? config.TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken(null)}
              ref={turnstileRef}
            />
          </div>
        )}
        <Button
          className={styles.forgottenPass}
          size="extra-small"
          variant="tertiary"
          data-testid="forgot-password-input"
          label="Forgot your password?"
          onClick={() => sendEvent("Forgot Password Link - Click")}
          to={formatUrl(
            "/forgot/password",
            {},
            {
              redirectUrl,
            }
          )}
        />
        <Checkbox
          id="loginByPasswordRememberMe"
          data-testid="remember-me-input"
          onChange={handleRememberMeClick}
          checked={rememberMe}
          label="Keep me signed in"
          wrapperClassName={styles.rememberMe}
          variant="medium"
        />
        <Button
          className={styles.loginBtn}
          data-testid="password-login-button"
          disabled={showTurnstile && !turnstileToken}
          label="Sign in"
          isLoading={isSigningIn}
          type="submit"
          onClick={() =>
            sendEvent("Sign in Button - Click", { staySignedIn: rememberMe })
          }
        />
      </form>
    </>
  );
};
